import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, List, Grid3x3 } from 'lucide-react';
import clsx from 'clsx';
import { navigate } from '../../../App';
import { Spinner } from '../../ui/Spinner';
import { EmptyState } from '../../ui/EmptyState';

// Same two views as the Businesses tab — keep them visually identical.
const VIEW_MODES = [
    { mode: 'rolodex', icon: <List size={14} />,    title: 'תצוגת גלילה אופקית' },
    { mode: 'compact', icon: <Grid3x3 size={14} />, title: 'תצוגת רשת קומפקטית' },
];

/**
 * Stores tab — lists the zone's stores. Like Businesses/Events/Barters it
 * offers both a rolodex (horizontal cards) and a compact grid view.
 */
export function StoresTab({
    stores,
    loading,
    // legacy props ignored
    infoCollapsed,   // eslint-disable-line no-unused-vars
    onToggleInfo,    // eslint-disable-line no-unused-vars
}) {
    const [viewMode, setViewMode] = useState('rolodex');
    const count = Array.isArray(stores) ? stores.length : 0;
    const hasStores = !loading && count > 0;
    return (
        <div className="space-y-6">
            <div className="flex items-start gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                    <ShoppingBag className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <div>
                    <h2 className="heading-lg text-ink leading-tight">
                        חנויות באזור
                        {count > 0 && (
                            <span className="ms-2 inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-pill bg-surface-muted text-ink-muted text-sm font-semibold align-middle">
                                {count}
                            </span>
                        )}
                    </h2>
                    <p className="text-sm text-ink-muted mt-1 max-w-xl">
                        חנויות מקומיות באזור — הזמינו ישירות מיצרנים ועסקים מקומיים.
                    </p>
                </div>
            </div>

            {/* View mode picker — mirrors EntityListTab's picker exactly */}
            {hasStores && (
                <div role="tablist" aria-label="מצב תצוגה" className="inline-flex items-center gap-1">
                    {VIEW_MODES.map(({ mode, icon, title }) => {
                        const active = viewMode === mode;
                        return (
                            <button
                                key={mode}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setViewMode(mode)}
                                title={title}
                                className={clsx(
                                    'group relative inline-flex items-center justify-center w-10 h-10 rounded-xl',
                                    'transition-[background-color,color,box-shadow] duration-200 ease-smooth',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                                    active
                                        ? 'bg-gradient-to-br from-brand-50 to-brand-100/70 text-brand-700 shadow-inset-soft ring-1 ring-brand-200/60'
                                        : 'text-ink-faint hover:text-brand-700 hover:bg-surface-muted/70',
                                )}
                            >
                                {React.cloneElement(icon, { size: 16, strokeWidth: active ? 2.5 : 2.25 })}
                                {active && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-pill bg-brand-500"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner size="xl" />
                </div>
            ) : stores.length === 0 ? (
                <EmptyState
                    icon={<ShoppingBag />}
                    title="אין חנויות באזור זה עדיין"
                    description="כשיוקמו חנויות באזור, הן יופיעו כאן."
                />
            ) : viewMode === 'compact' ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 sm:gap-2.5 w-full max-w-6xl mx-auto">
                    {stores.map((store) => (
                        <StoreCompactCard key={store.store_id} store={store} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
                    {stores.map((store) => (
                        <StoreCard key={store.store_id} store={store} />
                    ))}
                </div>
            )}
        </div>
    );
}

// Resolve the display fields off a store (+ its linked business) once.
function getStoreFields(store) {
    const biz = store._business;
    return {
        coverImg: store.cover_image_url || biz?.cover_image,
        profileImg: store.owner_profile_url,
        storeName: store.name || biz?.business_name,
        storeAbout: store.about || biz?.business_description,
        slug: store.store_slug || biz?.store_slug,
        isClosed: store.status === 'closed',
        category: biz?.business_categories?.[0],
    };
}

// Compact grid card — mirrors FilteredRolodexView's compact business card.
function StoreCompactCard({ store }) {
    const { coverImg, profileImg, storeName, slug } = getStoreFields(store);
    return (
        <motion.button
            type="button"
            onClick={() => navigate('store', `slug=${slug}`)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            className="group flex flex-col w-full bg-surface rounded-lg ring-1 ring-line-subtle hover:ring-brand-200 hover:shadow-card transition-[box-shadow,ring-color] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
            <div className="relative w-full">
                <div className="aspect-square w-full overflow-hidden bg-surface-muted rounded-t-lg">
                    {coverImg ? (
                        <img
                            src={coverImg}
                            alt={storeName}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.05]"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-50 to-secondary-50 text-brand-400">
                            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                    )}
                </div>
                {profileImg && (
                    <img
                        src={profileImg}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="absolute -bottom-2 end-1 w-6 h-6 rounded-pill object-cover ring-2 ring-white shadow-sm"
                    />
                )}
            </div>
            <div className="px-1.5 pt-2.5 pb-1.5">
                <h3 className="font-semibold text-2xs text-ink line-clamp-1 leading-tight text-right">{storeName}</h3>
            </div>
        </motion.button>
    );
}

function StoreCard({ store }) {
    const { coverImg, profileImg, storeName, storeAbout, slug, isClosed, category } = getStoreFields(store);

    // Mirrors Rolodex's EntityCard so store cards sit at the same size and
    // shape as business cards (compact horizontal row, square thumbnail).
    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => navigate('store', `slug=${slug}`)}
            className="group relative text-right block w-full bg-surface rounded-xl border border-line-subtle hover:border-brand-200 hover:shadow-card transition-[border-color,box-shadow] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
        >
            <div className="flex items-stretch gap-3 p-2.5">
                {/* Square thumbnail (profile avatar sits outside the clip box) */}
                <div className="relative shrink-0 w-20 sm:w-24">
                    <div className="aspect-square rounded-lg overflow-hidden bg-surface-muted">
                        {coverImg ? (
                            <img
                                src={coverImg}
                                alt={storeName}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.04]"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-50 to-secondary-50">
                                <ShoppingBag className="w-8 h-8 text-brand-400" strokeWidth={1.5} />
                            </div>
                        )}
                    </div>
                    {profileImg && (
                        <img
                            src={profileImg}
                            alt=""
                            aria-hidden="true"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="absolute -bottom-1.5 -end-1.5 w-7 h-7 rounded-pill object-cover ring-2 ring-white shadow-md"
                        />
                    )}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 flex flex-col justify-between py-0.5">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 min-w-0">
                            {category && (
                                <span className="text-2xs font-semibold uppercase tracking-wide text-brand-700 truncate">
                                    {category}
                                </span>
                            )}
                            {isClosed && (
                                <span className="inline-flex items-center text-2xs font-bold text-danger-600 bg-danger-50 ring-1 ring-danger-100 rounded-pill px-1.5 py-0.5 shrink-0">
                                    סגור
                                </span>
                            )}
                        </div>

                        <h3 className="text-sm font-bold text-ink leading-snug line-clamp-2">
                            {storeName}
                        </h3>

                        {storeAbout && (
                            <p className="text-xs text-ink-muted leading-snug line-clamp-1 mt-0.5">
                                {storeAbout}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </motion.button>
    );
}

export default StoresTab;
